// KinetiQ_UserGuide.jsx — revised per client's User Guide feedback (Oct 2026).
// - Clicking "User Guide" in the sidebar opens the guide on the Overview (welcome text) + Section 1.
//   There is no separate Overview / Login entry in the tree.
// - Button / control names are shown in bold (no quotation marks).
// - No "Red box" wording and no internal/dev notes in user-facing text.
// - Examples and Troubleshooting content are preserved.
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import '../styles/UserGuide.css';

const PLATFORM_URL = 'https://www.kinetiqmodel.com';
const DATA_FITTING_URL = `${PLATFORM_URL}/data-fitting#input`;

// ── Screenshot sizes ─────────────────────────────────────────────────
// Each figure can have its own maximum width (px). Images are never stretched
// beyond their real size, so they stay sharp; large ones are shrunk to this width.
// Tune any figure here in ONE place, or override a single figure inline:
//   <Shot src="/x.png" alt="..." width={480} />      exact max width in px
//   <Shot src="/x.png" alt="..." size="sm|md|lg" />  preset (420 / 560 / 760)
// Figures not listed here use the default cap from UserGuide.css (760px).
const SHOT_WIDTHS = {
  // small panel crops
  '/reaction_type.png': 440,
  '/rate_law.png': 440,
  '/reaction_order_exponents.png': 440,
  '/success_dialog.png': 440,
  '/initialize_with_example.png': 520,
  '/Choice_of_Data_Sets.png': 560,
  '/add_component.png': 440,
  '/reports.png': 640,
  '/running_the_programme.png': 560,
  // wide / full-page views
  '/03-input-empty.png': 640,
  '/model_input_page.png': 640,
  '/09-graph.png': 640,
  '/Parameters_Fitted_Kinetic_Values.png': 640,
  '/Optimize_the_Fit.png': 640,
  '/compare_model_results.png': 640,
  '/compare_graphs.png': 640,
  '/compare_results.png': 640,
  '/examples_batch1.png': 640,
  '/examples_batch2.png': 640,
  '/example_model_input.png': 640,
  '/13-examples-meoac.png': 640,
  '/14-examples-dme.png': 640,
  '/15-examples-pfr.png': 640,
  '/16-input-aa.png': 640,
};

const Shot = ({ src, alt, size, width }) => {
  const maxW = width || SHOT_WIDTHS[src];
  return (
    <div
      className={`ug-shot${size ? ` ug-shot--${size}` : ''}`}
      style={maxW ? { maxWidth: `min(100%, ${maxW}px)` } : undefined}
    >
      <img src={src} alt={alt} loading="lazy" />
    </div>
  );
};

const UrlBar = ({ url }) => (
  <div className="ug-url-bar">
    <span className="ug-url-label">URL</span>
    <a href={url} target="_blank" rel="noreferrer">{url}</a>
  </div>
);

const Section = ({ id, number, title, badge, children }) => (
  <section className="ug-section" id={id}>
    {badge && <div className="ug-badge ug-badge--green">{badge}</div>}
    <div className="ug-section-label">{number}</div>
    <h2>{title}</h2>
    <div className="ug-section-divider" />
    {children}
  </section>
);

/* ───────────────────────── SECTION 1 ───────────────────────── */
const DataModelInputSection = () => (
  <Section id="data-model-input" number="Section 1" title="Data and Model Input" badge="Data Fitting">
    <UrlBar url={DATA_FITTING_URL} />

    <div className="ug-subsection">
      <h3>Data Input Page</h3>
      <div className="ug-subsection-divider" />
      <p>
        The Data Input page opens with three panels:
        <strong> Components &amp; Stoichiometry</strong>, <strong>Choice of Data Sets</strong>, and
        <strong> Reaction Conditions</strong>.
      </p>
      <Shot src="/03-input-empty.png" alt="Data Input page" />
    </div>

    <div className="ug-steps">
      <div className="ug-step" id="dm-input">
        <div className="ug-step-number">1</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Components &amp; Stoichiometry</div>
          <p className="ug-step-desc">
            Enter reactants and products by searching by component name or formula. The user will be prompted to
            balance the elements by adjusting the stoichiometric coefficients. Add multiple reactions as needed and
            enter any <strong>Inert</strong> components in the system.
          </p>
          <p className="ug-step-desc">
            New components can be added to a local database (i.e., only in your login) if the component does not exist
            in the database. Search by CAS number to verify if the component exists under a different name.
          </p>
          <Shot src="/add_component.png" alt="Add a new component" />
        </div>
      </div>

      <div className="ug-step" id="dm-data">
        <div className="ug-step-number">2</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Choice of Data Sets</div>
          <Shot src="/Choice_of_Data_Sets.png" alt="Choice of Data Sets" />
          <p className="ug-step-desc">
            Enter single or multiple data sets by uploading a file or with <strong>Manual Data Entry – Batch</strong>.
            Match the correct columns to the respective components. A template for uploading a CSV file is available
            for download.
          </p>
          <p className="ug-step-desc">
            Note that <strong>Run Fitting</strong> at the top of the main screen only becomes functional after clicking
            <strong> Use This Data</strong> to certify usage of the data set. Data entered can be erased with the
            <strong> Clear All</strong> button. Additional rows can be added with the <strong>+ Row</strong> button, and
            any row can be deleted with the <strong>X</strong> at the end of each row.
          </p>
          <p className="ug-step-desc">
            Data Sets can be added with the <strong>Add Data Set</strong> option or removed with the <strong>X</strong>
            (Close) button.
          </p>
        </div>
      </div>

      <div className="ug-step" id="dm-conditions">
        <div className="ug-step-number">3</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Reaction Conditions</div>
          <p className="ug-step-desc">
            Use this panel to enter the reaction conditions under which the data was measured, in the appropriate
            units. In the case of multiple data sets, some reaction conditions — such as <strong>Temperature,
            Pressure, Reaction Volume, and Catalyst Weight</strong> — are entered specifically for each data set.
          </p>
        </div>
      </div>
    </div>

    <div className="ug-subsection" id="dm-model">
      <h3>Model Input Page</h3>
      <div className="ug-subsection-divider" />
      <p>
        The Model Input page has three panels as well: <strong>Reaction Type</strong>, <strong>Rate Law</strong>, and
        <strong> Reaction-order exponents</strong>.
      </p>
      <Shot src="/model_input_page.png" alt="Model Input page" />
      <div className="ug-steps">
        <div className="ug-step">
          <div className="ug-step-number">1</div>
          <div className="ug-step-body">
            <div className="ug-step-title">Reaction Type</div>
            <p className="ug-step-desc">
              Choose a <strong>reversible</strong> or <strong>irreversible</strong> mechanism for the reaction. In this
              panel, the user can also choose to specify the equilibrium constant in the given equation format
              (<code>ln(Keq) = X + Y/T(K)</code>) or allow the model to regress it from data.
            </p>
            <Shot src="/reaction_type.png" alt="Reaction Type" />
          </div>
        </div>

        <div className="ug-step">
          <div className="ug-step-number">2</div>
          <div className="ug-step-body">
            <div className="ug-step-title">Rate Law</div>
            <Shot src="/rate_law.png" alt="Rate Law" />
            <p className="ug-step-desc">
              Choose <strong>Power Law</strong> or <strong>LHHW</strong> (Langmuir-Hinshelwood-Hougen-Watson) for the
              rate equation. For LHHW, the user can specify the adsorption constants (Kᵢ) determined from experiments
              for each component, specific to their catalyst, or allow the model to regress them from data.
            </p>
          </div>
        </div>

        <div className="ug-step">
          <div className="ug-step-number">3</div>
          <div className="ug-step-body">
            <div className="ug-step-title">Reaction-order exponents</div>
            <Shot src="/reaction_order_exponents.png" alt="Reaction Order" />
            <p className="ug-step-desc">
              <strong>Exponent treatment:</strong> select whether the reaction orders (exponents) are regressed, kept
              fixed at 1, or specified by the user.
            </p>
            <p className="ug-step-desc">
              <strong>Objective Function Weights:</strong> specify the weights for the optimization, keep them the same
              for all species, or set them higher for product species. Each weight scales how much that species' error
              counts toward the loss the optimizer minimizes — a higher weight makes the fit work harder to match that
              species, at the cost of the others. The same weights are also applied when computing the R² shown after a
              run, so a heavily weighted species pulls the reported R² toward its own fit quality.
            </p>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* ───────────────────────── SECTION 2 ───────────────────────── */
const RunningProgramSection = () => (
  <Section id="running-program" number="Section 2" title="Running the Program" badge="Run">
    <div className="ug-steps">
      <div className="ug-step">
        <div className="ug-step-number">1</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Run Fitting</div>
          <Shot src="/running_the_programme.png" alt="Run Fitting" />
          <p className="ug-step-desc">
            The <strong>Run Fitting</strong> button at the top-right corner of the window stays greyed out until data is
            loaded. Default Model Input choices are in place until a user changes the specifications.
          </p>
        </div>
      </div>

      <div className="ug-step">
        <div className="ug-step-number">2</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Reset</div>
          <p className="ug-step-desc">
            <strong>Reset</strong> clears all the data and reloads the page. This is helpful when starting a new system,
            when the user would like to re-enter the reaction system or reactions, or when multiple inputs in Data and
            Model need to be re-entered.
          </p>
        </div>
      </div>

      <div className="ug-step">
        <div className="ug-step-number">3</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Stop and Reinitialize</div>
          <p className="ug-step-desc">
            Unlike Reset, the <strong>Stop</strong> and <strong>Reinitialize</strong> options pause an ongoing run and
            restart the fitting without having to re-enter the system data. <strong>Reinitialize</strong> sets the Model
            Input settings back to their defaults.
          </p>
        </div>
      </div>

      <div className="ug-step">
        <div className="ug-step-number">4</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Fitting result</div>
          <Shot src="/success_dialog.png" alt="Fitting Result" />
          <p className="ug-step-desc">
            A <strong>✓ Success</strong> dialog confirms <strong>Fitting completed successfully</strong> and generates
            the visual model vs. data comparison as well as the rate constant parameters. The <strong>Review</strong> button takes the user straight to the Results – Graph view.
          </p>
          <p className="ug-step-desc">
            In the case of an unsuccessful fitting result, the program highlights the input options that need to be
            re-evaluated and changed.
          </p>
        </div>
      </div>
    </div>
  </Section>
);

/* ───────────────────────── SECTION 3 ───────────────────────── */
const ResultsSection = () => (
  <Section id="understanding-results" number="Section 3" title="Understanding Results" badge="Results">
    <div className="ug-steps">
      <div className="ug-step">
        <div className="ug-step-number">1</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Graph — Composition Profiles and Fit Quality</div>
          <p className="ug-step-desc">
            The Graph tab plots experimental data against the model fit for every species, with sub-tabs for the
            Composition Profile Overview and the per-species Parity plots. Fit quality is assessed from the
            <strong> R²</strong> panel on the right, which rates the fit for each species: R² &lt; 0.80 is
            <strong> Poor</strong>, 0.80 &lt; R² &lt; 0.95 is <strong>Good</strong>, and R² &gt; 0.95 is
            <strong> Excellent</strong>.
          </p>
          <p className="ug-step-desc">
            Composition profiles or parity plots can be saved as an image by right-clicking on the figure. The
            <strong> Print</strong> button in the top-right corner prints the page to PDF.
          </p>
          <Shot src="/09-graph.png" alt="KinetiQ Graph results" />
        </div>
      </div>

      <div className="ug-step">
        <div className="ug-step-number">2</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Parameters — Fitted Kinetic Values</div>
          <p className="ug-step-desc">
            The Parameters page reports the results for the forward/backward rate constants, activation energies, the
            equilibrium constant, reaction-order exponents, and the corresponding expression used for each reaction.
          </p>
          <p className="ug-step-desc">
            Results can be downloaded to Excel, copied, or printed to PDF using the buttons at the top-right corner.
          </p>
          <Shot src="/Parameters_Fitted_Kinetic_Values.png" alt="Parameters — Fitted Kinetic Values" />
        </div>
      </div>

    {/*
      <div className="ug-step">
        <div className="ug-step-number">3</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Appendix</div>
          <p className="ug-step-desc">
            The Appendix collects run-specific information: <strong>Data Set Summary</strong>,
            <strong> Fitted vs Experimental Values</strong>, <strong>Predicted Timeseries</strong>, and
            <strong> Registered Components</strong>.
          </p>
        </div>
      </div>
    */}
    </div>
  </Section>
);

/* ───────────────────────── SECTION 4 ───────────────────────── */
const OptimizationSection = () => (
  <Section id="optimization-comparison" number="Section 4" title="Optimization and Comparison of Models" badge="Optimize">
    <div className="ug-steps">
      <div className="ug-step">
        <div className="ug-step-number">1</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Optimize the Fit (Optional)</div>
          <p className="ug-step-desc">
            This functionality is used to improve and optimize the model vs. data fit as seen in the composition profile view.
            The <strong>Fit Diagnostics</strong> section of the Optimize tab shows fit diagnostics per species in terms of R²
            and optimizer suggestions. Optimizer settings, such as the maximum number of iterations, the target R², or the
            choice of initial parameters, can be changed in the right-side panel to improve the model vs. data fit. The data
            fitting can be re-run from this panel in the top-right corner using the <strong>Re-run Fitting</strong> button.
          </p>
          <p className="ug-step-desc">
            To change any parameter from the <strong>Model Input</strong> tab in the Input section, the user can change the
            reaction type between reversible and irreversible, choose Power Law or LHHW, regress component exponents, or
            change species weights in optimization.
          </p>
          <Shot src="/Optimize_the_Fit.png" alt="Optimize the Fit" />
        </div>
      </div>

      <div className="ug-step">
        <div className="ug-step-number">2</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Multiple Data Sets</div>
          <p className="ug-step-desc">
            For a multi-data-set fit, each data set keeps its own conditions and results. The Graph view shows a separate
            composition-profile view for each Batch data set, and the diagnostics report <strong>one R² per data set</strong>.
            The same values are shown in the Graph and Optimize views.
          </p>
        </div>
      </div>

      <div className="ug-step">
        <div className="ug-step-number">3</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Compare Model Results</div>
          <Shot src="/compare_model_results.png" alt="Compare Model Results" />
          <p className="ug-step-desc">
            Each fit run with varying initial conditions can be saved using the <strong>Save Fit</strong> option. The saved
            fits can then be selected in <strong>Compare Fit</strong> to obtain a visual (graphical overlay) or tabular
            comparison of the model results.
          </p>
          <Shot src="/compare_graphs.png" alt="Compare Model Graphs" />
          <Shot src="/compare_results.png" alt="Compare Model Results" />
        </div>
      </div>

      <div className="ug-step" id="reports">
        <div className="ug-step-number">4</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Reports</div>
          <p className="ug-step-desc">
            Saved fits are also saved in <strong>Reports</strong>, along with the system and parameter results saved in
            Summary, PDF, and Excel form. The reaction system can be renamed by right-clicking on the system name.
            Unwanted fits can be deleted with the <strong>Delete</strong> button. To retrieve a particular system, click
            on <strong>Reopen</strong> to repopulate the entire reaction system with the reaction data entered.
          </p>
          <Shot src="/reports.png" alt="Reports page" />
        </div>
      </div>
    </div>
  </Section>
);

/* ─────────── SECTION 5 & 6 — Examples and Troubleshooting ─────────── */
const ExamplesSection = () => (
  <Section id="examples" number="Section 5" title="Examples" badge="Examples">
    <div className="ug-steps">
      <div className="ug-step">
        <div className="ug-step-number">1</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Batch and PFR Examples</div>
          <p className="ug-step-desc">
            There are 2 reaction system examples in the Batch Examples section and 1 in the PFR Examples section, all with
            pre-loaded data sets (single or multiple) and a chosen model selection. The examples are reaction systems from
            the literature, and the references are displayed. The user can select any system and click
            <strong> Run Fitting</strong> to obtain results.
          </p>
          <Shot src="/examples_batch1.png" alt="Batch and PFR Examples" />
          <Shot src="/examples_batch2.png" alt="Batch and PFR Examples" />
          <Shot src="/example_model_input.png" alt="Example Model Input" />
          <p className="ug-step-desc">
            The program has an additional feature that allows the user to click <strong>Unlock to edit</strong> near the top
            right corner of the screen to test other model options.
          </p>
          <Shot src="/initialize_with_example.png" alt="Initialize Example" />
          <p className="ug-step-desc">
            Another feature that allows the user to use examples as a starting point for their reaction system is
            <strong> Initialize with Example</strong> on the Input screen. This feature loads the example of the user’s
            choice, after which they can change the reaction system and model and upload their own system data.
          </p>
        </div>
      </div>

    {/*
      <div className="ug-step" id="meoac">
        <div className="ug-step-number">2</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Methyl Acetate (MeOAc) — Batch, Liquid Phase</div>
          <div className="ug-reaction">HOAc + MeOH ⇌ MeOAc + H₂O</div>
          <p className="ug-step-desc">
            Under Batch Examples, select the Methyl Acetate example. Review the populated reaction conditions, the
            single-data-set model options and the reference dataset, then click <strong>Run Fitting</strong>.
          </p>
          <Shot src="/13-examples-meoac.png" alt="Methyl Acetate example selection" />
        </div>
      </div>

      <div className="ug-step" id="dme">
        <div className="ug-step-number">3</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Dimethyl Ether (DME) Example</div>
          <div className="ug-reaction">2 MeOH ⇌ DME + H₂O</div>
          <p className="ug-step-desc">Methanol dehydration, vapor phase, T = 85°C.</p>
          <p className="ug-step-desc">
            In the Examples dialog, under Batch Examples, click the <strong>Dimethyl Ether DME</strong> tab. Choose either
            the <strong>Power Law</strong> or <strong>LHHW</strong> single-data-set card to auto-populate all fields, then
            upload your DME CSV and click <strong>Run Fitting</strong>, exactly as in Section 4.
          </p>
          <p className="ug-step-desc">
            <strong>Note:</strong> DME is vapor phase. Make sure your CSV uses a mole-fraction basis (xMeOH, xDME and xH₂O
            columns).
          </p>
          <Shot src="/14-examples-dme.png" alt="Examples dialog with the Dimethyl Ether DME tab selected" />
        </div>
      </div>

      <div className="ug-step" id="aa">
        <div className="ug-step-number">4</div>
        <div className="ug-step-body">
          <div className="ug-step-title">Acetic Acid (AA) — PFR, Multi-Reaction Example</div>
          <div className="ug-reaction">C₂H₆ + 1.5 O₂ → AA + H₂O, with additional oxidation reactions</div>
          <p className="ug-step-desc">
            The AA example is a complete simulation run from input to results. It demonstrates a PFR workflow with multiple
            reactions and species. Load the example, review the reaction network and conditions, click
            <strong> Run Fitting</strong>, then review the parity plots, fitted parameters and optimization diagnostics.
          </p>
          <Shot src="/15-examples-pfr.png" alt="Acetic Acid PFR example selection" />
          <Shot src="/16-input-aa.png" alt="Acetic Acid PFR input" />
        </div>
      </div>
    */}
    </div>
  </Section>
);

const troubleGroupStyle = {
  textAlign: 'left',
  letterSpacing: '0.08em',
  fontSize: '12px',
  textTransform: 'uppercase',
};

const TroubleGroup = ({ title }) => (
  <tr>
    <th colSpan={2} style={troubleGroupStyle}>{title}</th>
  </tr>
);

const TroubleshootingSection = () => (
  <Section id="troubleshooting" number="Section 6" title="Troubleshooting" badge="Reference">
    <table className="ug-trouble-table">
      <thead>
        <tr><th>Issue</th><th>Resolution</th></tr>
      </thead>
      <tbody>
        <TroubleGroup title="Login errors" />
        <tr>
          <td>Cannot open website</td>
          <td>Check the internet. Clear browser cache or open in an incognito window.</td>
        </tr>
        {/*
        <tr>
          <td>Verification email not received</td>
          <td>Check Spam/Junk. Wait 5–10 minutes, then re-register or use <strong>Resend</strong>.</td>
        </tr>
        */}
        <tr>
          <td>Login fails</td>
          <td>Confirm the email is verified. Use <strong>Forgot Password?</strong> on the login page.</td>
        </tr>

        <TroubleGroup title="Program errors" />
        <tr>
          <td>CSV upload fails</td>
          <td>Check the file format (CSV) and the column headers. Re-download the CSV template.</td>
        </tr>
        <tr>
          <td>Run Fitting button greyed out</td>
          <td>
            Upload a CSV file or manually enter the data and click <strong>Use This Data</strong>. The
            <strong> Run Fitting</strong> button activates only after the data and reaction are entered.
          </td>
        </tr>
        {/*
        <tr>
          <td>Poor R² fit rating</td>
          <td>Go to the Optimize tab. Tune the species weights, exclude flagged residual rows, or switch the fitting mode.</td>
        </tr>
        */}
        <tr>
          <td>504 Timeout error</td>
          <td>Switch from a Multiple Data Sets to a Single Data Set run, or reduce the data set size. Contact support if the error persists.</td>
        </tr>
        <tr>
          <td>Results page blank</td>
          <td>Ensure JavaScript is enabled. Try a different browser, or disable the ad-blocker for the browser.</td>
        </tr>
        <tr>
          <td>Compare Fit list is empty</td>
          <td>Save at least one fit from the Optimize tab first — only saved fits appear in Compare Fit.</td>
        </tr>
      </tbody>
    </table>
  </Section>
);

const SECTION_COMPONENTS = {
  'data-model-input': DataModelInputSection,
  'running-program': RunningProgramSection,
  'understanding-results': ResultsSection,
  'optimization-comparison': OptimizationSection,
  examples: ExamplesSection,
  troubleshooting: TroubleshootingSection,
};

const DEFAULT_SECTION = 'data-model-input';

const resolveHash = (hash) => (hash && SECTION_COMPONENTS[hash] ? hash : DEFAULT_SECTION);

const UserGuide = () => {
  const location = useLocation();
  const mainRef = useRef(null);
  const activeSection = resolveHash(location.hash ? location.hash.slice(1) : '');
  const ActiveComponent = SECTION_COMPONENTS[activeSection] || DataModelInputSection;
  // The welcome overview appears only when the user first opens the guide on Section 1.
  const showOverview = activeSection === DEFAULT_SECTION;

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [activeSection]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>
      <Sidebar />
      <div
        style={{
          marginLeft: '242px',
          width: 'calc(100% - 242px)',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Navbar activePage="app" />
        <div
          ref={mainRef}
          style={{ flex: 1, marginTop: '68px', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}
        >
          <div className="ug-container">
            <main className="ug-main">
              <div className="ug-main-inner">
                {showOverview && (
                  <div className="ug-hero">
                    <h1>KinetiQ User Guide</h1>
                    <p className="ug-hero-desc">
                      Welcome to KinetiQ — a web-based platform that fits, simulates, and helps you understand
                      catalytic reaction kinetics, right in your browser. This guide covers every step a new user
                      needs, with real platform screenshots.
                    </p>
                  </div>
                )}
                <ActiveComponent />
                <div className="ug-section-divider" style={{ marginTop: '60px' }} />
                {/*
                <footer className="ug-footer">
                  <p>End of KinetiQ User Guide · {PLATFORM_URL}</p>
                </footer>
                */}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;